// Customer portal — branded AI assistant chat widget (bottom-right).
//
// Feels like the retailer's own staff member. Fetches /api/customer/ai/config
// for the assistant's name + welcome (and whether it's enabled); chats over
// /api/customer/ai/chat (SSE). Write actions (log snag / send message) arrive
// as `pendingActions` and are shown as Confirm chips — nothing is sent until
// the customer confirms. Never exposes "AI"/TKOS — uses the retailer's brand
// colours (--brand-*) and the configured assistant name throughout.

interface AiConfig { ok?: boolean; name?: string; welcome?: string; enabled?: boolean }
interface PendingAction { tool: string; preview: string; confirmLabel: string; endpoint: string; payload: Record<string, unknown> }

const STYLE_ID = "cust-ai-style";

function injectStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    .cust-ai-fab { position: fixed; right: 18px; bottom: calc(18px + env(safe-area-inset-bottom,0px));
      z-index: 2147482000; width: 56px; height: 56px; border-radius: 50%; border: 0; cursor: pointer;
      background: var(--brand, var(--brand-primary, #E8760A)); color: #fff; box-shadow: 0 8px 24px rgba(0,0,0,.28);
      display: flex; align-items: center; justify-content: center; transition: transform .15s ease; }
    .cust-ai-fab:hover { transform: scale(1.06); }
    .cust-ai-fab svg { width: 26px; height: 26px; }
    .cust-ai-panel { position: fixed; right: 18px; bottom: calc(84px + env(safe-area-inset-bottom,0px));
      z-index: 2147482000; width: min(380px, calc(100vw - 24px)); height: min(560px, calc(100vh - 140px));
      display: none; flex-direction: column; border-radius: 16px; overflow: hidden;
      background: var(--surface, #fff); color: var(--text, #111); border: 1px solid var(--border, rgba(0,0,0,.12));
      box-shadow: 0 18px 50px rgba(0,0,0,.35); }
    .cust-ai-panel.is-open { display: flex; }
    .cust-ai-head { display: flex; align-items: center; gap: 10px; padding: 12px 14px;
      background: var(--brand, var(--brand-primary, #E8760A)); color: #fff; }
    .cust-ai-av { flex: none; width: 34px; height: 34px; border-radius: 50%; background: rgba(255,255,255,.22);
      display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: .85rem; }
    .cust-ai-htext { flex: 1; min-width: 0; }
    .cust-ai-hname { font-weight: 800; font-size: .95rem; line-height: 1.1; }
    .cust-ai-hsub { font-size: .7rem; opacity: .85; }
    .cust-ai-x { background: transparent; border: 0; color: #fff; font-size: 1.4rem; line-height: 1; cursor: pointer; }
    .cust-ai-body { flex: 1; overflow-y: auto; padding: 12px; display: flex; flex-direction: column; gap: 10px;
      background: var(--bg, var(--surface2, #f6f6f7)); }
    .cust-ai-msg { max-width: 86%; padding: 9px 12px; border-radius: 14px; font-size: .88rem; line-height: 1.45; white-space: pre-wrap; }
    .cust-ai-msg--bot { align-self: flex-start; background: var(--surface, #fff); color: var(--text, #111); border: 1px solid var(--border, rgba(0,0,0,.1)); border-bottom-left-radius: 4px; }
    .cust-ai-msg--me { align-self: flex-end; background: var(--brand, var(--brand-primary, #E8760A)); color: #fff; border-bottom-right-radius: 4px; }
    .cust-ai-actions { align-self: flex-start; max-width: 92%; display: flex; flex-direction: column; gap: 8px; }
    .cust-ai-action { border: 1px solid var(--border, rgba(0,0,0,.14)); background: var(--surface, #fff); border-radius: 12px; padding: 10px; font-size: .82rem; }
    .cust-ai-action-prev { color: var(--text2, #333); margin-bottom: 8px; }
    .cust-ai-action-btns { display: flex; gap: 8px; }
    .cust-ai-btn { border: 0; border-radius: 9px; padding: .42rem .8rem; font-size: .8rem; font-weight: 700; cursor: pointer; }
    .cust-ai-btn--go { background: var(--brand, var(--brand-primary, #E8760A)); color: #fff; }
    .cust-ai-btn--no { background: transparent; color: var(--text-muted, #888); border: 1px solid var(--border, rgba(0,0,0,.14)); }
    .cust-ai-foot { display: flex; gap: 8px; padding: 10px; border-top: 1px solid var(--border, rgba(0,0,0,.1)); background: var(--surface, #fff); }
    .cust-ai-input { flex: 1; resize: none; border: 1px solid var(--border, rgba(0,0,0,.16)); border-radius: 12px;
      padding: .5rem .7rem; font: inherit; font-size: .88rem; outline: none; max-height: 96px; background: var(--bg, #fff); color: var(--text, #111); }
    .cust-ai-input:focus { border-color: var(--brand, var(--brand-primary, #E8760A)); }
    .cust-ai-send { flex: none; border: 0; border-radius: 12px; width: 40px; background: var(--brand, var(--brand-primary, #E8760A)); color: #fff; cursor: pointer; font-size: 1.1rem; }
    .cust-ai-send:disabled { opacity: .5; cursor: default; }
  `;
  document.head.appendChild(s);
}

function initials(name: string): string {
  return (name.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase()) || "A";
}
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}

const CHAT_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1 3.6-11.5 8.38 8.38 0 0 1 12.5 7.7z"/></svg>';

export function mountAiWidget(host: HTMLElement, retailerName = ""): void {
  // Remove any prior instance (shell re-renders) before mounting fresh.
  document.getElementById("cust-ai-widget")?.remove();

  void (async () => {
    let cfg: AiConfig;
    try {
      const resp = await fetch("/api/customer/ai/config", { credentials: "include" });
      if (!resp.ok) return;
      cfg = (await resp.json()) as AiConfig;
    } catch { return; }
    if (!cfg || cfg.enabled === false) return; // assistant disabled by the retailer

    const name = cfg.name || "Aria";
    const welcome = cfg.welcome || `Hi! I'm ${name}. Ask me anything about your kitchen project — your quote, payments, delivery, or anything else.`;

    injectStyle();
    const wrap = document.createElement("div");
    wrap.id = "cust-ai-widget";
    wrap.innerHTML = `
      <button class="cust-ai-fab" type="button" aria-label="Chat with ${esc(name)}">${CHAT_ICON}</button>
      <div class="cust-ai-panel" role="dialog" aria-label="${esc(name)}">
        <div class="cust-ai-head">
          <span class="cust-ai-av" aria-hidden="true">${esc(initials(name))}</span>
          <div class="cust-ai-htext"><div class="cust-ai-hname">${esc(name)}</div><div class="cust-ai-hsub">Here to help with your kitchen</div></div>
          <button class="cust-ai-x" type="button" aria-label="Close">&times;</button>
        </div>
        <div class="cust-ai-body" data-role="body"></div>
        <div class="cust-ai-foot">
          <textarea class="cust-ai-input" data-role="input" rows="1" placeholder="Ask ${esc(name)} anything about your kitchen…"></textarea>
          <button class="cust-ai-send" data-role="send" aria-label="Send">↑</button>
        </div>
      </div>`;
    host.appendChild(wrap);

    const fab = wrap.querySelector<HTMLButtonElement>(".cust-ai-fab")!;
    const panel = wrap.querySelector<HTMLElement>(".cust-ai-panel")!;
    const bodyEl = wrap.querySelector<HTMLElement>("[data-role='body']")!;
    const input = wrap.querySelector<HTMLTextAreaElement>("[data-role='input']")!;
    const sendBtn = wrap.querySelector<HTMLButtonElement>("[data-role='send']")!;
    const closeBtn = wrap.querySelector<HTMLButtonElement>(".cust-ai-x")!;

    const history: { role: "user" | "assistant"; content: string }[] = [];
    let greeted = false;
    let busy = false;

    const scrollDown = (): void => { bodyEl.scrollTop = bodyEl.scrollHeight; };
    const addBubble = (cls: "bot" | "me", text: string): HTMLElement => {
      const el = document.createElement("div");
      el.className = `cust-ai-msg cust-ai-msg--${cls}`;
      el.textContent = text;
      bodyEl.appendChild(el);
      scrollDown();
      return el;
    };

    function renderActions(actions: PendingAction[]): void {
      const box = document.createElement("div");
      box.className = "cust-ai-actions";
      box.innerHTML = actions.map((a, i) => `
        <div class="cust-ai-action" data-i="${i}">
          <div class="cust-ai-action-prev">${esc(a.preview)}</div>
          <div class="cust-ai-action-btns">
            <button class="cust-ai-btn cust-ai-btn--go" data-act="go">${esc(a.confirmLabel || "Send")}</button>
            <button class="cust-ai-btn cust-ai-btn--no" data-act="no">Not now</button>
          </div>
        </div>`).join("");
      bodyEl.appendChild(box);
      scrollDown();
      box.querySelectorAll<HTMLElement>(".cust-ai-action").forEach((card, i) => {
        const a = actions[i]!;
        card.querySelector("[data-act='no']")?.addEventListener("click", () => card.remove());
        card.querySelector("[data-act='go']")?.addEventListener("click", () => {
          const btns = card.querySelector(".cust-ai-action-btns");
          if (btns) btns.innerHTML = "Sending…";
          void fetch("/api" + a.endpoint, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" }, body: JSON.stringify(a.payload),
          }).then((r) => {
            const who = retailerName || "the team";
            card.remove();
            addBubble("bot", r.ok
              ? (a.tool === "log_snag"
                  ? `Done — I've logged that with ${who}. They'll take a look and be in touch.`
                  : `Done — your message is on its way to ${who}.`)
              : "Sorry, I couldn't send that just now. Please try again in a moment.");
          }).catch(() => { card.remove(); addBubble("bot", "Sorry, I couldn't send that just now."); });
        });
      });
    }

    async function send(): Promise<void> {
      const text = input.value.trim();
      if (!text || busy) return;
      busy = true; sendBtn.disabled = true;
      input.value = "";
      addBubble("me", text);
      history.push({ role: "user", content: text });
      const bot = addBubble("bot", "");
      let acc = "";
      try {
        const resp = await fetch("/api/customer/ai/chat", {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: text, history: history.slice(0, -1) }),
        });
        if (!resp.ok || !resp.body) throw new Error("no stream");
        const reader = resp.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        let pending: PendingAction[] | null = null;
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() ?? "";
          for (const line of lines) {
            const m = line.replace(/^data: /, "");
            if (!m.trim()) continue;
            try {
              const o = JSON.parse(m) as { t?: string; pendingActions?: PendingAction[]; error?: string; done?: boolean };
              if (o.t) { acc += o.t; bot.textContent = acc; scrollDown(); }
              if (o.error) { acc += (acc ? "\n" : "") + o.error; bot.textContent = acc; }
              if (o.pendingActions) pending = o.pendingActions;
            } catch { /* ignore partial */ }
          }
        }
        if (!acc) { bot.textContent = "…"; }
        history.push({ role: "assistant", content: acc });
        if (pending && pending.length) renderActions(pending);
      } catch {
        bot.textContent = "Sorry, I had trouble responding just now. Please try again.";
      } finally {
        busy = false; sendBtn.disabled = false; input.focus();
      }
    }

    const open = (): void => {
      panel.classList.add("is-open");
      if (!greeted) { addBubble("bot", welcome); greeted = true; }
      input.focus();
    };
    const close = (): void => panel.classList.remove("is-open");

    fab.addEventListener("click", () => (panel.classList.contains("is-open") ? close() : open()));
    closeBtn.addEventListener("click", close);
    sendBtn.addEventListener("click", () => void send());
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); }
    });
    input.addEventListener("input", () => {
      input.style.height = "auto";
      input.style.height = Math.min(input.scrollHeight, 96) + "px";
    });
  })();
}
